import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';

const SERVICES_MENU = [
  { icon: 'fas fa-code',        title: 'Software Development',      desc: 'Custom business software and enterprise platforms', to: '/services' },
  { icon: 'fas fa-mobile-alt',  title: 'Mobile App Development',    desc: 'iOS and Android apps with Mobile Money support',    to: '/services' },
  { icon: 'fas fa-cubes',       title: 'System Development',        desc: 'Case, document, records and M&E systems',          to: '/services' },
  { icon: 'fas fa-server',      title: 'Web Hosting',               desc: 'Managed hosting for 200+ businesses in Uganda',    to: '/services' },
  { icon: 'fas fa-globe',       title: 'Domain Registration',       desc: '.com, .co.ug, .ug and 50+ domain extensions',      to: '/services' },
  { icon: 'fas fa-paint-brush', title: 'Website Designing',         desc: 'Fast, mobile-first sites built to convert',        to: '/services' },
];


function Navbar() {
  const [dropdown, setDropdown]     = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileAcc, setMobileAcc]   = useState(null);
  const headerRef = useRef(null);
  const { pathname } = useLocation();
  const { dark, toggle: toggleTheme } = useTheme();

  useEffect(() => {
    setDropdown(null);
    setMobileOpen(false);
    setMobileAcc(null);
  }, [pathname]);

  useEffect(() => {
    const close = e => {
      if (headerRef.current && !headerRef.current.contains(e.target)) {
        setDropdown(null);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const toggleDrop = name => setDropdown(d => (d === name ? null : name));
  const isActive   = path => pathname === path || (path !== '/' && pathname.startsWith(path));

  return (
    <header className="site-header" ref={headerRef}>
      <nav className="navbar-corporate">
        <div className="container">
          <div className="nav-inner">

            {/* ── Logo ── */}
            <Link to="/" className="nav-brand">
              <img
                src="/logo-white.png"
                alt="Salama Consult"
                className="nav-logo-img"
                onError={e => { e.target.style.display = 'none'; }}
              />

            </Link>

            {/* ── Desktop links ── */}
            <ul className="nav-links d-none d-lg-flex">
              <li>
                <Link to="/" className={pathname === '/' ? 'active' : ''}>Home</Link>
              </li>

              {/* Services — dropdown inside the li */}
              <li className="position-relative">
                <button
                  className={`nav-btn${dropdown === 'services' || isActive('/services') ? ' active' : ''}`}
                  onClick={() => toggleDrop('services')}
                >
                  Services
                  <i className={`fas fa-chevron-down nav-chevron${dropdown === 'services' ? ' rotated' : ''}`} />
                </button>

                {dropdown === 'services' && (
                  <div className="mega-menu-wrap" role="navigation" aria-label="Services menu">
                    <div className="mega-header">
                      <h6>Our Services</h6>
                      <Link to="/services" onClick={() => setDropdown(null)}>View all →</Link>
                    </div>
                    <div className="mega-grid">
                      {SERVICES_MENU.map(s => (
                        <Link
                          key={s.title}
                          to={s.to}
                          className="mega-item"
                          onClick={() => setDropdown(null)}
                        >
                          <div className="mega-item-icon"><i className={s.icon} /></div>
                          <div>
                            <div className="mega-item-title">{s.title}</div>
                            <div className="mega-item-desc">{s.desc}</div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </li>

              <li>
                <Link to="/industries" className={isActive('/industries') ? 'active' : ''}>Industries</Link>
              </li>

              <li>
                <Link to="/portfolio" className={isActive('/portfolio') ? 'active' : ''}>Portfolio</Link>
              </li>
              <li>
                <Link to="/about" className={isActive('/about') ? 'active' : ''}>About</Link>
              </li>
              <li>
                <Link to="/contact" className={isActive('/contact') ? 'active' : ''}>Contact</Link>
              </li>
            </ul>

            {/* ── Desktop controls ── */}
            <div className="nav-controls d-none d-lg-flex">
              <button className="theme-toggle" onClick={toggleTheme} aria-label="Toggle theme">
                <i className={dark ? 'fas fa-sun' : 'fas fa-moon'} />
              </button>
              <Link to="/contact" className="btn-nav-cta">Get in Touch</Link>
            </div>

            {/* ── Mobile controls ── */}
            <div className="d-flex d-lg-none align-items-center gap-2">
              <button className="theme-toggle" onClick={toggleTheme} aria-label="Toggle theme">
                <i className={dark ? 'fas fa-sun' : 'fas fa-moon'} />
              </button>
              <button
                className="hamburger-btn"
                onClick={() => setMobileOpen(o => !o)}
                aria-label="Toggle navigation"
              >
                <i className={mobileOpen ? 'fas fa-times' : 'fas fa-bars'} />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* ── Mobile Menu ── */}
      {mobileOpen && (
        <div className="mobile-menu-wrap" role="navigation">
          <div className="container">
            <Link to="/" className="mobile-link">Home</Link>

            <div>
              <button
                className="mobile-accordion-btn"
                onClick={() => setMobileAcc(a => a === 'services' ? null : 'services')}
              >
                Services
                <i className={`fas fa-chevron-${mobileAcc === 'services' ? 'up' : 'down'}`} style={{ fontSize: '0.7rem' }} />
              </button>
              {mobileAcc === 'services' && (
                <div className="mobile-accordion-content">
                  {SERVICES_MENU.map(s => (
                    <Link key={s.title} to={s.to} className="mobile-sub-link">
                      <i className={s.icon} />{s.title}
                    </Link>
                  ))}
                  <Link to="/services" className="mobile-sub-link" style={{ fontWeight: 700, color: 'rgba(255,255,255,0.85)' }}>
                    View All Services →
                  </Link>
                </div>
              )}
            </div>

            <Link to="/industries" className="mobile-link">Industries</Link>
            <Link to="/portfolio"  className="mobile-link">Portfolio</Link>

            <Link to="/about"    className="mobile-link">About</Link>
            <Link to="/contact"  className="mobile-link">Contact</Link>

            <div className="mt-3">
              <Link to="/contact" className="btn-nav-cta d-block text-center" style={{ padding: '0.75rem' }}>
                Get in Touch
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

export default Navbar;
